import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Waves, Zap, Hourglass, Droplets, HeartHandshake, ChevronLeft, ChevronRight, Flower2, Gem, Leaf } from "lucide-react";
import { TreatmentRow } from "../../components/public/TreatmentRow";
import { useLanguage } from "@/i18n/LanguageContext";
import { useBooking } from "@/context/BookingContext";
import { api, API } from "@/lib/api";
import { useSiteMedia } from "@/context/SiteMediaContext";

const USP_ICONS = [Sparkles, Waves, Zap, Hourglass, Droplets, HeartHandshake];
const HERO_USP_ICONS = [Flower2, Gem, Leaf];

const TESTIMONIALS = [
  { name: "Sophie M.", pt: "Um atendimento incrível, resultados visíveis.", fr: "Un accueil incroyable, des résultats visibles." },
  { name: "Julie R.", pt: "Carolina é atenciosa e muito profissional.", fr: "Carolina est attentionnée et très professionnelle." },
];

export default function Home() {
  const media = useSiteMedia();
  const { T, lang } = useLanguage();
  const { openBooking } = useBooking();
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [videoTestimonials, setVideoTestimonials] = useState([]);
  const [tIndex, setTIndex] = useState(0);
  const [vIndex, setVIndex] = useState(0);

  useEffect(() => {
    api.get("/public/services").then((r) => setServices(r.data.slice(0, 4))).catch(() => {});
    api.get(`/public/blog?lang=${lang}`).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    api.get("/public/testimonials").then((r) => setVideoTestimonials(r.data)).catch(() => {});
  }, [lang]);

  return (
    <div data-testid="home-page">
      <section className="grid grid-cols-1 lg:grid-cols-12 min-h-screen pt-20">
        <div className="lg:col-span-6 relative flex flex-col justify-center px-6 lg:px-16 py-16 lg:py-0 order-2 lg:order-1 overflow-hidden">
          <img
            src={media.logo}
            alt=""
            aria-hidden="true"
            className="absolute -bottom-6 -right-16 lg:-right-24 w-[280px] lg:w-[400px] opacity-[0.16] pointer-events-none select-none"
          />
          <div className="relative z-10">
            <p className="text-[11px] tracking-[0.25em] uppercase text-[#9B6179] mb-6" data-testid="hero-eyebrow">{T.hero.eyebrow}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#6F3F55] leading-[1.05] mb-6" data-testid="hero-title">
              {T.hero.title_line1}<br />{T.hero.title_line2}
            </h1>
            <p className="text-base text-[#090708]/60 max-w-sm mb-8">{T.hero.subtitle}</p>
            <button
              onClick={() => openBooking()}
              data-testid="hero-cta-button"
              className="rounded-[2px] inline-flex items-center gap-3 bg-[#090708] text-[#F7F0E8] px-8 py-4 text-[11px] tracking-[0.2em] uppercase self-start hover:bg-[#6F3F55] transition-colors duration-300 border border-[#E8C47A]/30 mb-10"
            >
              {T.hero.cta} <ArrowRight className="w-3.5 h-3.5" />
            </button>

            <div className="w-10 border-t border-[#090708]/15 mb-6" />
            <p className="font-script text-2xl text-[#6F3F55] leading-snug mb-2" data-testid="hero-quote">"{T.hero.quote}"</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#090708]/40 mb-10">— {T.about.signature}</p>

            <div className="flex flex-wrap gap-x-8 gap-y-4">
              {T.hero.usp.map((u, i) => {
                const Icon = HERO_USP_ICONS[i];
                return (
                  <div key={i} className="flex items-center gap-2" data-testid={`hero-usp-${i}`}>
                    <Icon className="w-4 h-4 text-[#9B6179]" strokeWidth={1.5} />
                    <span className="text-[11px] tracking-[0.05em] uppercase text-[#090708]/60 leading-tight">
                      {u.t}<br /><span className="text-[#090708]/40">{u.d}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="lg:col-span-6 relative h-[60vh] lg:h-screen order-1 lg:order-2">
          <img src={media.home_hero} alt="Studio Carolina Aguiar" className="w-full h-full object-cover" />
          <div className="hidden lg:block absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#090708]/25 to-transparent pointer-events-none" />
          <div className="hidden lg:block absolute top-12 right-10 text-right">
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#F7F0E8]/90 leading-relaxed" data-testid="hero-side-tagline">
              {T.hero.side_line1}<br />{T.hero.side_line2}
            </p>
          </div>
          <p className="hidden lg:block absolute bottom-8 right-10 text-[10px] tracking-[0.25em] uppercase text-[#F7F0E8]/70">
            Bagneux · Paris
          </p>
        </div>
      </section>

      <section className="py-10 border-t border-b border-[#090708]/8 overflow-hidden">
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 max-w-5xl mx-auto px-6">
          {T.usp.map((usp, i) => {
            const Icon = USP_ICONS[i];
            return (
              <span key={i} className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#090708]/50" data-testid={`usp-${i}`}>
                <Icon className="w-4 h-4 text-[#9B6179]" strokeWidth={1.5} /> {usp}
              </span>
            );
          })}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center px-6 lg:px-16 py-12 md:py-16 max-w-[1600px] mx-auto">
        <div className="lg:col-span-7 relative h-[420px] lg:h-[600px] overflow-hidden">
          <img src={media.home_about} alt="Carolina Aguiar" className="w-full h-full object-cover object-top" />
        </div>
        <div className="lg:col-span-5 lg:-ml-12 z-10 p-8 lg:p-12 bg-[#F7F0E8] border border-[#090708]/10">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#9B6179] mb-4">{T.about.eyebrow}</p>
          <h2 className="font-serif text-3xl lg:text-4xl text-[#9B6179] mb-5 leading-tight">{T.about.title}</h2>
          <p className="text-base text-[#090708]/60 leading-relaxed mb-6">{T.about.text}</p>
          <p className="font-script text-2xl text-[#9B6179] mb-1">{T.about.signature}</p>
          <p className="text-[11px] tracking-widest uppercase text-[#090708]/40 mb-8">{T.about.role}</p>
          <Link to="/sobre" data-testid="home-about-cta" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#090708] border-b border-[#090708]/20 pb-1 hover:border-[#E8C47A]">
            {T.about.cta} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      <section className="px-6 lg:px-16 py-12 md:py-16 max-w-[1600px] mx-auto">
        <div className="mb-10">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#9B6179] mb-3">{T.treatments.eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#9B6179]">{T.treatments.title}</h2>
        </div>
        <div className="flex flex-col divide-y divide-[#090708]/8">
          {services.map((s, i) => <TreatmentRow key={s.id} service={s} index={i} />)}
        </div>
        <Link to="/tratamentos" className="inline-flex items-center gap-2 mt-10 text-[11px] tracking-[0.2em] uppercase text-[#090708] border-b border-[#090708]/20 pb-1 hover:border-[#E8C47A]" data-testid="home-treatments-cta">
          {T.treatments.view} {T.nav.treatments} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </section>

      <section className="px-6 lg:px-16 py-12 md:py-16 max-w-[1600px] mx-auto bg-[#F7F0E8]">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-[#9B6179] mb-3">{T.blog.eyebrow}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#9B6179]">{T.blog.title}</h2>
          </div>
          <Link to="/blog" data-testid="home-blog-cta" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#090708] border-b border-[#090708]/20 pb-1 hover:border-[#E8C47A]">
            {T.blog.view_all} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {posts.length > 0 && (
          <div className="grid sm:grid-cols-3 gap-8">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`home-blog-post-${p.slug}`} className="group block">
                <div className="aspect-[4/5] overflow-hidden mb-4">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#9B6179] mb-2">{p.category}</p>
                <p className="font-serif text-xl text-[#090708] mb-2 leading-snug">{p.title}</p>
                <p className="text-sm text-[#090708]/55 line-clamp-2">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="grid grid-cols-1 md:grid-cols-12 gap-6 px-6 lg:px-16 py-12 md:py-16 max-w-[1600px] mx-auto items-start">
        <div className="md:col-span-7 aspect-[3/4] overflow-hidden">
          <img src={media.home_results} alt="results" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-5 md:mt-24">
          <div className="aspect-[4/5] overflow-hidden mb-8">
            <img src={media.home_treatment} alt="results detail" className="w-full h-full object-cover" />
          </div>
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#9B6179] mb-3">{T.results.eyebrow}</p>
          <h2 className="font-serif text-2xl lg:text-3xl text-[#9B6179] mb-5">{T.results.title}</h2>
          <Link to="/resultados" data-testid="home-results-cta" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-[#090708] border-b border-[#090708]/20 pb-1 hover:border-[#E8C47A]">
            {T.results.cta} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      <section className="py-12 md:py-14 px-6 bg-[#9B6179]">
        <div className="max-w-2xl mx-auto text-center">
          {videoTestimonials.length > 0 ? (
            <>
              <video
                key={videoTestimonials[vIndex].id}
                src={`${API}/public/testimonials/${videoTestimonials[vIndex].id}/video`}
                controls
                className="w-full max-w-sm mx-auto rounded-[2px] border border-[#F7F0E8]/20 mb-6 bg-black"
                data-testid="home-video-testimonial"
              />
              <p className="text-[11px] tracking-widest uppercase text-[#F7F0E8]/70 mb-6">{videoTestimonials[vIndex].client_name}</p>
              {videoTestimonials.length > 1 && (
                <div className="flex items-center justify-center gap-6">
                  <button onClick={() => setVIndex((vIndex - 1 + videoTestimonials.length) % videoTestimonials.length)} data-testid="video-testimonial-prev" className="text-[#F7F0E8]/60 hover:text-[#E8C47A]"><ChevronLeft className="w-4 h-4" /></button>
                  <span className="text-[11px] tracking-widest uppercase text-[#F7F0E8]/40">{T.results.testimonials_title}</span>
                  <button onClick={() => setVIndex((vIndex + 1) % videoTestimonials.length)} data-testid="video-testimonial-next" className="text-[#F7F0E8]/60 hover:text-[#E8C47A]"><ChevronRight className="w-4 h-4" /></button>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="font-serif text-2xl lg:text-3xl text-[#F7F0E8] leading-relaxed mb-6">
                "{lang === "pt" ? TESTIMONIALS[tIndex].pt : TESTIMONIALS[tIndex].fr}"
              </p>
              <p className="text-[11px] tracking-widest uppercase text-[#F7F0E8]/70 mb-6">{TESTIMONIALS[tIndex].name}</p>
              <button onClick={() => setTIndex((tIndex + 1) % TESTIMONIALS.length)} data-testid="testimonial-next" className="text-[11px] tracking-widest uppercase text-[#F7F0E8]/60 hover:text-[#E8C47A]">
                {T.results.testimonials_title} →
              </button>
            </>
          )}
        </div>
      </section>

      <section className="py-10 px-6 text-center">
        <h2 className="font-serif text-3xl lg:text-4xl text-[#9B6179] mb-6">{T.contact.title}</h2>
        <button onClick={() => openBooking()} data-testid="bottom-cta-button" className="rounded-[2px] inline-flex items-center gap-3 bg-[#090708] text-[#F7F0E8] px-8 py-4 text-[11px] tracking-[0.2em] uppercase hover:bg-[#6F3F55] transition-colors duration-300 border border-[#E8C47A]/30">
          {T.hero.cta} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </section>
    </div>
  );
}
